import FormControlLabel from "@mui/material/FormControlLabel";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import {useEffect, useId, useState} from "react";
import {useSearchTranslation} from "../search/SearchContext";
import RadioGroup from "@mui/material/RadioGroup";
import Radio from "@mui/material/Radio";
import FormControl from "@mui/material/FormControl";
import PropTypes from "prop-types";


/**
 * A component used to set the image size when downloading one or more files.
 * Contains a radio group with options for original, web, print, and custom image
 * sizes. Also contains two text fields for custom width and height input.
 *
 * @param {boolean} multiple A boolean indicating whether one or multiple files are being downloaded.
 * @param {function} callback A callback function that receives the preferred image size.
 * @returns {React.JSX.Element} A JSX element representing the component.
 */
const SetImageProperties = ({multiple, callback}) => {

    const t = useSearchTranslation();
    const widthFieldId = useId();
    const heightFieldId = useId();

    const [customSize, setCustomSize] = useState({width: 0, height: 0});
    const [radioOption, setRadioOption] = useState('custom');

    /**
     * Handles when the user enters custom size values for width- and height.
     * @param event
     * @param axis
     */
    const handleSizeChange = (event, axis) => {
        const {target} = event;
        const {value} = target;
        let w = customSize.width;
        let h = customSize.height;
        if (axis === 'width') {
            w = value
        } else {
            h = value;
        }
        callback({
            width: parseInt(w),
            height: parseInt(h),
            quality: 80,
            selected: true
        });
        setCustomSize({width: w, height: h});
    };

    /**
     * Handles change in image size type, when the user clicks a radio button.
     * @param event
     */
    const handleRadioChange = event => {
        let w;
        let h;
        let quality;
        const {target} = event;
        const {value: size, checked} = target;
        setRadioOption(size);
        if (size === 'original') {
            w = null;
            h = null;
            quality = 80;
        } else if (size === 'web') {
            w = 2000;
            h = 0;
            quality = 80;
        } else if (size === 'print') {
            w = 4000;
            h = 0;
            quality = 100;
        } else {
            w = 2000;
            h = 1500;
            quality = 80;
        }

        callback({
            width: w,
            height: h,
            quality: quality,
            selected: checked
        });
    };

    /**
     * Hook used to set the initial custom size value,
     * and default to this setting when the dialog opens.
     */
    useEffect(() => {
        const w = 2000;
        const h = 1500;
        setCustomSize({width: w, height: h});
        setRadioOption("custom");
        callback({width: w, height: h, quality: 80, selected: true});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box>
            <Divider orientation={"horizontal"} sx={{width: '100%', marginTop: '8px', marginBottom: '8px'}}/>
            {multiple &&
                <Typography
                    variant="subtitle1"
                    sx={{
                        mt: 2,
                        mb: 1
                    }}>
                    {t('oneOrMoreIsImage', 'Vi ser at en eller flere av de merkede filene er bilder')}
                </Typography>
            }
            <Box>
                <FormControl>
                    <RadioGroup
                        aria-labelledby="custom-sizes-group"
                        defaultValue={"custom"}
                        name="radio-custom-sizes-group"
                        value={radioOption}
                        onChange={handleRadioChange}>
                        <FormControlLabel value="original" control={<Radio/>}
                                          label={t('imageSizeOriginal', 'Original')}/>
                        <FormControlLabel value="web" control={<Radio/>} label={t('imageSizeOptionWeb', 'Web')}/>
                        <FormControlLabel value="print" control={<Radio/>} label={t('imageSizeOptionPrint', 'Trykk')}/>
                        <FormControlLabel value="custom" control={<Radio/>}
                                          label={t('imageSizeOptionCustom', 'Egendefinert')}/>
                    </RadioGroup>
                </FormControl>

                <Stack direction="row" sx={{marginTop: '8px'}}>
                    <TextField id={widthFieldId}
                               disabled={radioOption !== 'custom'}
                               label={t('imageWidthPx', "Bredde (px)")}
                               variant="standard"
                               value={customSize.width}
                               onChange={(e) => handleSizeChange(e, 'width')}/>
                    <Box sx={{
                        ml: 2
                    }}/>
                    <TextField id={heightFieldId}
                               disabled={radioOption !== 'custom'}
                               label={t('imageHeightPx', "Høyde (px)")}
                               variant="standard"
                               value={customSize.height}
                               onChange={e => handleSizeChange(e, 'height')}/>
                </Stack>
            </Box>
        </Box>
    );
};

SetImageProperties.propTypes = {
    multiple: PropTypes.bool.isRequired,
    callback: PropTypes.func.isRequired
};

export {SetImageProperties};
